import React, { useState, useEffect } from 'react';
import { Row, Col, Spinner, Alert } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import ccmService from '../../services/ccmService';
import './CCMDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CCMDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [practiceId, setPracticeId] = useState('');

  useEffect(() => {
    // Get practice ID from URL or user context
    const urlParams = new URLSearchParams(window.location.search);
    const pid = urlParams.get('practiceId') || '1'; // Default to "1" for testing and demo purposes
    setPracticeId(pid);
    
    if (pid) {
      fetchDashboardStats(pid);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchDashboardStats = async (pid) => {
    try {
      setLoading(true);
      const response = await ccmService.getDashboardStats(pid);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const enrollmentChartData = stats?.enrollment_trend ? {
    labels: stats.enrollment_trend.map(t => t.month),
    datasets: [{
      label: 'Enrollments',
      data: stats.enrollment_trend.map(t => t.enrollments),
      borderColor: 'rgb(75, 192, 192)',
      backgroundColor: 'rgba(75, 192, 192, 0.2)',
      tension: 0.1
    }]
  } : null;

  if (loading) {
    return (
      <div className="ccm-db-container">
        <div className="ccm-db-loading">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className="ccm-db-container">
      <div className="ccm-db-page-title">
        <h3 className="ccm-db-title-text">CCM Dashboard</h3>
      </div>

      {!practiceId && (
        <Alert variant="warning" className="ccm-db-alert ccm-db-alert-warning">
          Please select a practice to view CCM dashboard
        </Alert>
      )}

      {practiceId && (
        <>
          {/* Statistics Cards */}
          <Row className="ccm-db-stats-row">
            <Col md={3}>
              <div className="ccm-db-stat-card">
                <div className="ccm-db-stat-title">Total Enrolled</div>
                <div className="ccm-db-stat-value">{stats?.total_enrolled || 0}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="ccm-db-stat-card">
                <div className="ccm-db-stat-title">Active Enrollments</div>
                <div className="ccm-db-stat-value text-success">{stats?.active_enrollments || 0}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="ccm-db-stat-card">
                <div className="ccm-db-stat-title">Total Service Time</div>
                <div className="ccm-db-stat-value">{formatTime(stats?.total_service_time || 0)}</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="ccm-db-stat-card">
                <div className="ccm-db-stat-title">Total Revenue</div>
                <div className="ccm-db-stat-value text-primary">${(stats?.total_revenue || 0).toFixed(2)}</div>
              </div>
            </Col>
          </Row>

          {/* Enrollment Trend Chart */}
          {enrollmentChartData && (
            <Row className="mb-4">
              <Col>
                <div className="ccm-db-chart-card">
                  <div className="ccm-db-chart-header">
                    <h5 className="ccm-db-chart-title">Enrollment Trend (Last 3 Months)</h5>
                  </div>
                  <div className="ccm-db-chart-body">
                    <Line data={enrollmentChartData} options={{
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'top',
                        },
                        title: {
                          display: true,
                          text: 'Monthly Enrollments'
                        }
                      }
                    }} />
                  </div>
                </div>
              </Col>
            </Row>
          )}

          {/* Additional Stats */}
          <Row className="ccm-db-additional-stats">
            <Col md={6}>
              <div className="ccm-db-additional-card">
                <div className="ccm-db-additional-header">
                  <h5 className="ccm-db-additional-title">Pending Enrollments</h5>
                </div>
                <div className="ccm-db-additional-body">
                  <div className="ccm-db-additional-value">{stats?.pending_enrollments || 0}</div>
                </div>
              </div>
            </Col>
            <Col md={6}>
              <div className="ccm-db-additional-card">
                <div className="ccm-db-additional-header">
                  <h5 className="ccm-db-additional-title">Total Claims</h5>
                </div>
                <div className="ccm-db-additional-body">
                  <div className="ccm-db-additional-value">{stats?.total_claims || 0}</div>
                </div>
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

export default CCMDashboard;

